/*
 * Cosmo core: loaded on every page as one small ES module.
 * Drawers, sticky header, announcement rotator, dropdowns, cart, predictive search.
 * Page-specific behaviour (product page, collection filters) lives in its own module,
 * loaded only by the sections that need it.
 */

const { routes, cartType } = window.Cosmo || { routes: {}, cartType: 'drawer' };
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const desktop = window.matchMedia('(min-width: 1000px)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

export const announce = (message) => {
  const region = document.getElementById('cosmo-live');
  if (!region) return;
  region.textContent = '';
  requestAnimationFrame(() => { region.textContent = message; });
};

const debounce = (fn, wait = 250) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
};

/* ---------- Drawers (native <dialog>) ---------- */

export const openDrawer = (id, opener) => {
  const dialog = document.getElementById(id);
  if (!dialog || dialog.open) return;
  dialog._opener = opener || document.activeElement;
  if (dialog.dataset.nonModal !== undefined) {
    dialog.show();
  } else {
    dialog.showModal();
  }
  // Focus the dialog itself so no focus ring lands on the first button
  if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
  dialog.focus({ preventScroll: true });
  dialog.dispatchEvent(new CustomEvent('drawer:open'));
};

export const closeDrawer = (dialog) => {
  if (!dialog?.open) return;
  dialog.close();
};

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-drawer-open]');
  if (opener) {
    const id = opener.dataset.drawerOpen;
    if (id === 'cart-drawer' && cartType !== 'drawer') return;
    event.preventDefault();
    const dialog = document.getElementById(id);
    if (dialog?.open && opener.hasAttribute('data-drawer-toggle')) {
      closeDrawer(dialog);
    } else {
      openDrawer(id, opener);
    }
    return;
  }

  const closer = event.target.closest('[data-drawer-close]');
  if (closer) {
    closeDrawer(closer.closest('dialog'));
    return;
  }

  // Click on the backdrop of a modal dialog closes it
  if (event.target instanceof HTMLDialogElement && event.target.classList.contains('drawer')) {
    const r = event.target.getBoundingClientRect();
    const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
    if (!inside) closeDrawer(event.target);
  }
});

document.addEventListener('close', (event) => {
  const dialog = event.target;
  if (!(dialog instanceof HTMLDialogElement)) return;
  document.querySelectorAll(`[data-drawer-open="${dialog.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
  dialog._opener?.focus?.({ preventScroll: true });
}, true);

document.addEventListener('drawer:open', (event) => {
  document.querySelectorAll(`[data-drawer-open="${event.target.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
}, true);

/* ---------- <details> helpers ---------- */

// Open on hover for desktop dropdown menus
document.querySelectorAll('details[data-hover-open]').forEach((details) => {
  let timer;
  details.addEventListener('mouseenter', () => {
    if (!finePointer.matches) return;
    clearTimeout(timer);
    details.open = true;
  });
  details.addEventListener('mouseleave', () => {
    if (!finePointer.matches) return;
    timer = setTimeout(() => { details.open = false; }, 120);
  });
});

// Close open dropdowns when clicking elsewhere or pressing Escape
document.addEventListener('click', (event) => {
  document.querySelectorAll('details[data-hover-open][open], details[data-close-on-outside-click][open]').forEach((d) => {
    if (!d.contains(event.target)) d.open = false;
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  // Search inputs swallow the first Escape to clear their text; close the drawer regardless
  const openDialog = [...document.querySelectorAll('dialog[open]')].pop();
  if (openDialog) {
    event.preventDefault();
    closeDrawer(openDialog);
    return;
  }
  document.querySelectorAll('details[data-hover-open][open], details[data-close-on-outside-click][open]').forEach((d) => {
    d.open = false;
    d.querySelector('summary')?.focus();
  });
});

// Footer columns start collapsed on phones
const syncFooterColumns = () => {
  document.querySelectorAll('[data-footer-column]').forEach((d) => { d.open = desktop.matches; });
};
syncFooterColumns();
desktop.addEventListener('change', syncFooterColumns);

/* ---------- Sticky header ---------- */

class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.lastY = window.scrollY;
    this.updateHeight();
    new ResizeObserver(() => this.updateHeight()).observe(this);
    window.addEventListener('scroll', () => requestAnimationFrame(() => this.onScroll()), { passive: true });
    this.onScroll();
  }

  updateHeight() {
    const bottom = Math.round(this.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--header-bottom', `${Math.max(bottom, 0)}px`);
    document.documentElement.style.setProperty('--header-sticky-height', `${this.offsetHeight}px`);
  }

  onScroll() {
    const y = Math.max(window.scrollY, 0);
    this.classList.toggle('is-scrolled', y > 10);
    const menuOpen = document.getElementById('menu-drawer')?.open;
    const delta = y - this.lastY;
    // Ignore tiny jitters (phone momentum, address bar) so the header doesn't flicker
    if (Math.abs(delta) < 6 && y > this.offsetHeight) return;
    if (this.hasAttribute('data-hide-on-scroll')) {
      const hide = delta > 0 && y > this.offsetHeight * 2 && !menuOpen;
      this.classList.toggle('is-hidden', hide);
    }
    this.lastY = y;
    this.updateHeight();
  }
}
customElements.define('sticky-header', StickyHeader);

/* ---------- Announcement bar ---------- */

class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.messages = [...this.querySelectorAll('.announcement-bar__message')];
    this.index = 0;
    this.querySelectorAll('[data-dir]').forEach((b) => b.addEventListener('click', () => {
      this.show(this.index + Number(b.dataset.dir));
      this.restart();
    }));
    this.delay = Number(this.dataset.autoplay || 0);
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.restart());
    this.restart();
  }

  show(i) {
    const n = this.messages.length;
    const current = this.messages[this.index];
    this.index = (i + n) % n;
    const swap = () => this.messages.forEach((m, j) => { m.hidden = j !== this.index; });
    if (reducedMotion.matches || !current || current.hidden) return swap();
    // Old message lifts away, then the next one fades in
    current.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-10px)' }], { duration: 250, easing: 'cubic-bezier(0.55, 0.055, 0.675, 0.19)' }).onfinish = swap;
  }

  stop() {
    clearInterval(this.timer);
  }

  restart() {
    this.stop();
    if (this.delay && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.timer = setInterval(() => this.show(this.index + 1), this.delay);
    }
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* ---------- Cart ---------- */

const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const data = await response.json();
  if (!response.ok) throw Object.assign(new Error(data.description || data.message || 'Request failed'), { data });
  return data;
};

export const Cart = {
  // Re-renders every cart view on the page (drawer, cart page) through the Section Rendering API
  async refresh() {
    const views = [...document.querySelectorAll('[data-cart-section]')];
    const ids = [...new Set(views.map((v) => v.dataset.sectionId).filter(Boolean))];
    const root = routes.root.replace(/\/$/, '');
    const [cart, ...markup] = await Promise.all([
      fetchJSON(`${routes.cart}.js`),
      ...ids.map((id) => fetch(`${root}/?section_id=${id}`).then((r) => r.text())),
    ]);
    const sections = Object.fromEntries(ids.map((id, i) => [id, markup[i]]));
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    views.forEach((view) => {
      const html = sections[view.dataset.sectionId];
      if (!html) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(`[data-cart-section][data-section-id="${view.dataset.sectionId}"]`);
      if (fresh) view.replaceChildren(...fresh.childNodes);
    });
    this.last = cart;
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    return cart;
  },

  async add(formData) {
    const response = await fetch(`${routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
    const data = await response.json();
    if (!response.ok) throw Object.assign(new Error(data.description || data.message), { data });
    await this.refresh();
    return data;
  },

  async remove(variantId) {
    await fetchJSON(`${routes.cartChange}.js`, { method: 'POST', body: JSON.stringify({ id: String(variantId), quantity: 0 }) });
    return this.refresh();
  },

  async change(line, quantity) {
    await fetchJSON(`${routes.cartChange}.js`, { method: 'POST', body: JSON.stringify({ line, quantity }) });
    return this.refresh();
  },

  async updateNote(note) {
    return fetchJSON(`${routes.cart}/update.js`, { method: 'POST', body: JSON.stringify({ note }) });
  },
};

// Any add-to-cart form on the site goes through the drawer when it is enabled
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.action.includes('/cart/add') || cartType !== 'drawer') return;
  if (form.hasAttribute('data-cart-toggle')) return;
  event.preventDefault();
  const button = form.querySelector('[type="submit"]');
  const errorBox = form.querySelector('[data-form-error]');
  button?.setAttribute('aria-busy', 'true');
  if (errorBox) errorBox.hidden = true;
  try {
    await Cart.add(new FormData(form));
    announce(window.Cosmo?.strings?.addedToCart || 'Added to cart');
    openDrawer('cart-drawer', button);
  } catch (error) {
    if (errorBox) {
      errorBox.textContent = error.message || window.Cosmo?.strings?.error;
      errorBox.hidden = false;
    }
  } finally {
    button?.removeAttribute('aria-busy');
  }
});

// Quick add toggles (related products): add or remove quietly, without opening the cart drawer
const syncCartToggles = (cart) => {
  const inCart = new Set(cart.items.map((item) => String(item.variant_id)));
  document.querySelectorAll('form[data-cart-toggle]').forEach((form) => {
    const button = form.querySelector('[type="submit"]');
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    const added = inCart.has(form.dataset.variantId);
    button.classList.toggle('is-added', added);
    button.setAttribute('aria-pressed', String(added));
    button.setAttribute('aria-label', added ? button.dataset.labelRemove : button.dataset.labelAdd);
  });
};

document.addEventListener('cart:updated', (event) => syncCartToggles(event.detail.cart));

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-cart-toggle')) return;
  event.preventDefault();
  const button = form.querySelector('[type="submit"]');
  if (!button || button.getAttribute('aria-busy') === 'true') return;
  const removing = button.classList.contains('is-added');
  button.setAttribute('aria-busy', 'true');
  try {
    if (removing) {
      await Cart.remove(form.dataset.variantId);
      announce(window.Cosmo?.strings?.removedFromCart || 'Removed from cart');
    } else {
      await Cart.add(new FormData(form));
      announce(window.Cosmo?.strings?.addedToCart || 'Added to cart');
    }
  } catch (error) {
    announce(error.message || window.Cosmo?.strings?.error);
  } finally {
    button.removeAttribute('aria-busy');
  }
  // Cart.refresh already synced the other toggles; this one was busy, so sync it now
  syncCartToggles(Cart.last || (await fetchJSON(`${routes.cart}.js`)));
});

class QuantityStepper extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.querySelectorAll('[data-step]').forEach((b) => b.addEventListener('click', () => {
      this.input.value = Math.max(0, Number(this.input.value) + Number(b.dataset.step));
      this.commit();
    }));
    this.input.addEventListener('change', () => this.commit());
  }

  async commit() {
    const line = Number(this.dataset.line);
    if (!line) {
      this.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const view = this.closest('[data-cart-section]');
    view?.setAttribute('aria-busy', 'true');
    try {
      await Cart.change(line, Number(this.input.value));
    } finally {
      document.querySelectorAll('[data-cart-section]').forEach((v) => v.removeAttribute('aria-busy'));
    }
  }
}
customElements.define('quantity-stepper', QuantityStepper);

// Order note saves as you type; remove buttons set the line to zero
document.addEventListener('input', debounce((event) => {
  if (event.target.matches?.('[data-cart-note]')) Cart.updateNote(event.target.value);
}, 500));

document.addEventListener('click', async (event) => {
  const remove = event.target.closest('[data-cart-remove]');
  if (!remove) return;
  const view = remove.closest('[data-cart-section]');
  view?.setAttribute('aria-busy', 'true');
  try {
    await Cart.change(Number(remove.dataset.cartRemove), 0);
  } finally {
    document.querySelectorAll('[data-cart-section]').forEach((v) => v.removeAttribute('aria-busy'));
  }
});

// Toggle panels (cart note / shipping estimate). Opening one closes its siblings.
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-toggle-panel]');
  if (!toggle) return;
  const open = toggle.getAttribute('aria-expanded') !== 'true';
  toggle.parentElement.querySelectorAll('[data-toggle-panel]').forEach((t) => {
    t.setAttribute('aria-expanded', 'false');
    const panel = document.getElementById(t.getAttribute('aria-controls'));
    if (panel) panel.hidden = true;
  });
  toggle.setAttribute('aria-expanded', String(open));
  const panel = document.getElementById(toggle.getAttribute('aria-controls'));
  if (panel) {
    panel.hidden = !open;
    if (open) panel.querySelector('textarea, select, input')?.focus();
  }
});

class ShippingEstimator extends HTMLElement {
  connectedCallback() {
    this.country = this.querySelector('[name="country"]');
    this.province = this.querySelector('[name="province"]');
    this.zip = this.querySelector('[name="zip"]');
    this.results = this.querySelector('[data-results]');
    const preferred = this.country.dataset.default;
    if (preferred) this.country.value = preferred;
    this.country.addEventListener('change', () => this.syncProvinces());
    this.syncProvinces();
    this.querySelector('[data-estimate]').addEventListener('click', () => this.estimate());
  }

  syncProvinces() {
    const option = this.country.selectedOptions[0];
    let provinces = [];
    try { provinces = JSON.parse(option?.dataset.provinces || '[]'); } catch (e) { provinces = []; }
    this.province.hidden = provinces.length === 0;
    this.province.replaceChildren(...provinces.map(([value, label]) => new Option(label, value)));
  }

  async estimate() {
    const params = new URLSearchParams({
      'shipping_address[country]': this.country.value,
      'shipping_address[province]': this.province.hidden ? '' : this.province.value,
      'shipping_address[zip]': this.zip.value,
    });
    const button = this.querySelector('[data-estimate]');
    const strings = window.Cosmo?.strings || {};
    button.setAttribute('aria-busy', 'true');
    this.results.textContent = '';
    try {
      await fetch(`${routes.cart}/prepare_shipping_rates.json?${params}`, { method: 'POST' });
      let rates = null;
      for (let i = 0; i < 10 && rates === null; i++) {
        const response = await fetch(`${routes.cart}/async_shipping_rates.json?${params}`);
        const data = await response.json();
        if (response.ok && data.shipping_rates) rates = data.shipping_rates;
        else await new Promise((r) => setTimeout(r, 600));
      }
      if (!rates || rates.length === 0) {
        this.results.textContent = strings.noRates || '';
      } else {
        const list = document.createElement('ul');
        rates.forEach((rate) => {
          const li = document.createElement('li');
          const price = Number(rate.price) === 0 ? (strings.free || 'Free') : `${rate.price} ${rate.currency}`;
          li.textContent = `${rate.name}: ${price}`;
          list.append(li);
        });
        this.results.replaceChildren(list);
      }
    } catch (error) {
      this.results.textContent = strings.error || '';
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}
customElements.define('shipping-estimator', ShippingEstimator);

/* ---------- Mobile menu panel ---------- */

const menu = document.getElementById('menu-drawer');
if (menu) {
  const header = document.querySelector('sticky-header');
  menu.addEventListener('drawer:open', () => {
    header?.classList.add('menu-is-open');
    header?.classList.remove('is-hidden');
    document.documentElement.classList.add('scroll-lock');
  });
  menu.addEventListener('close', () => {
    header?.classList.remove('menu-is-open');
    document.documentElement.classList.remove('scroll-lock');
  });
  desktop.addEventListener('change', () => { if (desktop.matches) closeDrawer(menu); });
}

/* ---------- Predictive search ---------- */

// Words shoppers type that don't match how products are named. Only a whole query is swapped, and the store is
// asked for both, e.g. "pimples" searches "(pimples) OR (acne)". Product tags (catalog-taxonomy/sync_search_tags.py)
// carry the rest of the shopper words.
const SEARCH_SYNONYMS = {
  'vitamin a': 'retinol', 'pimple': 'acne', 'pimples': 'acne', 'breakout': 'acne', 'breakouts': 'acne',
  'pimple patch': 'patch', 'pimple patches': 'patch', 'acne patch': 'patch', 'acne patches': 'patch',
  'shower gel': 'body wash', 'bath gel': 'body wash', 'whitening': 'brightening',
  'wrinkle': 'anti aging', 'wrinkles': 'anti aging', 'fine lines': 'anti aging', 'gluta': 'glutathione',
  'itchy scalp': 'dandruff', 'hair loss': 'hair fall', 'oil control': 'oily skin', 'dark circles': 'eye cream',
  'sunblock': 'sunscreen', 'sun block': 'sunscreen', 'sun cream': 'sunscreen', 'suncream': 'sunscreen',
  'moisturizer': 'moisturiser', 'moisturiser': 'moisturizer', 'face wash': 'cleanser', 'facewash': 'cleanser',
  'exfoliant': 'exfoliating', 'peeling': 'exfoliating', 'chapstick': 'lip balm', 'cica': 'centella',
  'centella': 'cica', 'txa': 'tranexamic', 'vit c': 'vitamin c', 'vitc': 'vitamin c',
  'roundlab': 'round lab', 'somebymi': 'some by mi', 'loreal': "l'oreal",
};
const expandSearch = (q) => {
  const extra = SEARCH_SYNONYMS[q.toLowerCase().replace(/\s+/g, ' ').trim()];
  return extra ? `(${q.trim()}) OR (${extra})` : q;
};
// the full results page gets the same swap
document.addEventListener('submit', (event) => {
  const form = event.target;
  const input = form.querySelector?.('input[name="q"]');
  if (input && new URL(form.action, location.href).pathname.endsWith('/search')) input.value = expandSearch(input.value);
});

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.input?.addEventListener('input', debounce(() => this.search(), 300));
    this.closest('dialog')?.addEventListener('drawer:open', () => setTimeout(() => this.input?.focus(), 50));
  }

  async search() {
    const q = this.input.value.trim();
    this.controller?.abort();
    if (!q) {
      this.results.innerHTML = '';
      return;
    }
    this.controller = new AbortController();
    this.setAttribute('aria-busy', 'true');
    try {
      const url = `${routes.predictiveSearch}?q=${encodeURIComponent(expandSearch(q))}&section_id=predictive-search&resources[type]=product,query,collection&resources[limit]=4&resources[limit_scope]=each&resources[options][fields]=title,product_type,variants.title,vendor,tag`;
      const html = await fetch(url, { signal: this.controller.signal }).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-predictive-search]');
      this.results.replaceChildren(...(content ? content.childNodes : []));
    } catch (error) {
      if (error.name !== 'AbortError') this.results.innerHTML = '';
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);

/* ---------- Product rows: arrow buttons scroll their row ---------- */

const syncRowButtons = (row) => {
  const wrap = row.closest('[data-rail]') || row.parentElement;
  const max = row.scrollWidth - row.clientWidth - 2;
  wrap.querySelectorAll('[data-scroll]').forEach((button) => {
    const dir = Number(button.dataset.scroll);
    button.disabled = dir < 0 ? row.scrollLeft <= 2 : row.scrollLeft >= max;
  });
  const progress = wrap.querySelector('.row-progress');
  if (progress && row.scrollWidth > 0) {
    const share = Math.min(1, row.clientWidth / row.scrollWidth);
    progress.style.setProperty('--progress-size', `${share * 100}%`);
    progress.style.setProperty('--progress-offset', `${(row.scrollLeft / row.scrollWidth) * progress.clientWidth}px`);
  }
};

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-scroll]');
  if (!button) return;
  const row = (button.closest('[data-rail]') || button.parentElement).querySelector('[data-row]');
  if (!row) return;
  row.scrollBy({ left: Number(button.dataset.scroll) * row.clientWidth * 0.9, behavior: 'smooth' });
});

/* Look-ahead photos for sideways rows. Native lazy loading waits until a card is almost on screen, so a fast swipe
   shows photos arriving one by one. Once a row is close to the viewport, the photos one screen-width ahead (either
   way) start loading, so the next cards are ready before the shopper swipes to them. Rows further down the page
   load nothing until they get close. */
const lookAhead = new WeakMap();
const startLookAhead = (row) => {
  if (lookAhead.has(row) || !('IntersectionObserver' in window)) return;
  const ahead = new IntersectionObserver((entries) => entries.forEach((e) => {
    if (!e.isIntersecting) return;
    e.target.loading = 'eager';
    ahead.unobserve(e.target);
  }), { root: row, rootMargin: '0px 100% 0px 100%' });
  const scan = () => row.querySelectorAll('img[loading="lazy"]:not([data-ahead])').forEach((img) => {
    img.dataset.ahead = '';
    ahead.observe(img);
  });
  lookAhead.set(row, scan);
  scan();
};
const nearRows = 'IntersectionObserver' in window
  ? new IntersectionObserver((entries) => entries.forEach((e) => {
      if (!e.isIntersecting) return;
      startLookAhead(e.target);
      nearRows.unobserve(e.target);
    }), { rootMargin: '600px 0px' })
  : null;

const watchRows = (root = document) => {
  root.querySelectorAll('[data-row]').forEach((row) => {
    if (row.dataset.watched) {
      lookAhead.get(row)?.(); // new cards were added to a row that is already looking ahead
      return;
    }
    row.dataset.watched = 'true';
    row.addEventListener('scroll', () => syncRowButtons(row), { passive: true });
    syncRowButtons(row);
    nearRows?.observe(row);
  });
};
watchRows();
window.addEventListener('resize', () => document.querySelectorAll('[data-row]').forEach(syncRowButtons));
export { watchRows };

/* ---------- Related products (Shopify recommendations) ---------- */

class RelatedProducts extends HTMLElement {
  connectedCallback() {
    const load = async () => {
      try {
        const html = await fetch(this.dataset.url).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('related-products');
        if (fresh && fresh.innerHTML.trim()) {
          this.className = fresh.className; // the filled version carries the blush band's rhythm classes
          this.innerHTML = fresh.innerHTML;
          watchRows(this);
          if (this.querySelector('form[data-cart-toggle]')) syncCartToggles(Cart.last || (await fetchJSON(`${routes.cart}.js`)));
        }
      } catch (error) {
        /* recommendations are optional */
      }
    };
    new IntersectionObserver((entries, observer) => {
      if (entries.some((e) => e.isIntersecting)) {
        observer.disconnect();
        load();
      }
    }, { rootMargin: '600px 0px' }).observe(this);
  }
}
customElements.define('related-products', RelatedProducts);

/* ---------- Motion: scroll reveals ---------- */
// [selector, reveal kind, stagger in ms between siblings]
const REVEALS = [
  ['.product-card', 'fade', 0],
  ['.brand-grid__item', 'fade-slow', 0],
  ['.editorial__media', 'arrive', 0],
  ['.ingredients__item', 'rise', 55],
  ['.guide__fact', 'rise', 55],
  ['.footer__wordmark-text', 'arrive', 0],
];

const revealObserver = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    const el = entry.target;
    el.classList.add('is-revealed');
    revealObserver.unobserve(el);
  });
}, { rootMargin: '0px 0px -6% 0px', threshold: 0.12 });

const scanReveals = (root = document) => {
  REVEALS.forEach(([selector, kind, stagger]) => {
    root.querySelectorAll(selector).forEach((el) => {
      if (el.classList.contains('reveal')) return;
      el.classList.add('reveal', `reveal--${kind}`);
      if (stagger) {
        const index = [...el.parentElement.children].indexOf(el);
        el.style.setProperty('--reveal-delay', `${Math.min(index, 6) * stagger}ms`);
      }
      revealObserver.observe(el);
    });
  });
};

scanReveals();
// Content added later (recommendations, theme editor, AJAX) gets the same treatment
new MutationObserver(debounce(() => scanReveals(), 60)).observe(document.body, { childList: true, subtree: true });
window.CosmoMotionReady = true;

/* ---------- Motion: animated accordions (<details data-accordion>) ---------- */

const OPEN_TIMING = { duration: 460, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' };
const CLOSE_TIMING = { duration: 400, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' };

export const toggleDetails = (details, open = !details.open || details.classList.contains('is-closing')) => {
  const content = details.querySelector(':scope > :not(summary)');
  const startHeight = details.getBoundingClientRect().height;
  details._motion?.forEach((a) => a.cancel());
  details._motion = null;

  if (reducedMotion.matches || !content) {
    details.classList.remove('is-closing', 'is-animating');
    details.open = open;
    return;
  }

  // Measure the resting height of the target state, then keep the content rendered while it animates
  details.open = open;
  const endHeight = details.getBoundingClientRect().height;
  details.open = true;
  details.classList.add('is-animating');
  details.classList.toggle('is-closing', !open);

  const height = details.animate({ height: [`${startHeight}px`, `${endHeight}px`] }, open ? OPEN_TIMING : CLOSE_TIMING);
  const fade = open
    ? content.animate([{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }], { duration: 320, delay: 70, easing: OPEN_TIMING.easing, fill: 'backwards' })
    : content.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-4px)' }], { duration: 260, easing: CLOSE_TIMING.easing, fill: 'forwards' });
  details._motion = [height, fade];

  height.onfinish = () => {
    details._motion = null;
    fade.cancel();
    details.classList.remove('is-animating', 'is-closing');
    if (!open) details.open = false;
  };
};
window.Cosmo = Object.assign(window.Cosmo || {}, { toggleDetails, openDrawer, closeDrawer, Cart });

document.addEventListener('click', (event) => {
  if (!event.target.closest('[data-scroll-top]')) return;
  window.scrollTo({ top: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
});

document.addEventListener('click', (event) => {
  const summary = event.target.closest('details[data-accordion] > summary');
  if (!summary) return;
  event.preventDefault();
  toggleDetails(summary.parentElement);
});

